import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlanLock } from './usePlanLock';
import { refreshPlanState } from './refresh-plan-state';

// A payment provider sends the browser back before its confirmation has
// always reached central-api, so a return from checkout asks a few times.
const RETURN_FROM_CHECKOUT_ATTEMPTS = 6;

/**
 * Where a locked organisation stands after coming back from a checkout:
 * - `idle`: nothing to wait for (not locked, or no checkout behind us);
 * - `checking`: asking central-api whether the payment has been confirmed;
 * - `processing`: asked, and the organisation is still locked. The payment
 *   may well have gone through, so the plans must not be offered again.
 */
export type PaymentConfirmationState = 'idle' | 'checking' | 'processing';

export interface PaymentConfirmation {
  state: PaymentConfirmationState;
  /** Ask once more; for the member who was told the payment is processing. */
  checkAgain: () => void;
}

/**
 * Follows the plan state after a return from checkout. `returnedFromCheckout`
 * is read once, on mount.
 */
export function usePaymentConfirmation(
  returnedFromCheckout: boolean
): PaymentConfirmation {
  const { locked } = usePlanLock();
  const [state, setState] = useState<PaymentConfirmationState>(
    locked && returnedFromCheckout ? 'checking' : 'idle'
  );
  const mounted = useRef(true);

  const poll = useCallback((attempts: number) => {
    setState('checking');
    refreshPlanState({ attempts })
      .then((stillLocked) => stillLocked)
      // The check itself failed: the lock is as it was.
      .catch(() => true)
      .then((stillLocked) => {
        if (mounted.current) {
          setState(stillLocked ? 'processing' : 'idle');
        }
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (locked && returnedFromCheckout) {
      poll(RETURN_FROM_CHECKOUT_ATTEMPTS);
    }
    return () => {
      mounted.current = false;
    };
    // Once per visit: the poll itself ends as soon as the lock lifts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAgain = useCallback(() => poll(1), [poll]);

  return { state: locked ? state : 'idle', checkAgain };
}
